/**
 * Compact property card for map marker hover/tap.
 * Shows: name, remaining kWh + SOC %, status badge, one-line risk, "View property →".
 * Energy semantics: kWh + % (no %-only); status from existing canon.
 */

import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export type MapMarkerStatus = 'live' | 'warning' | 'offline' | 'unknown';

export interface PropertyMapHoverCardProps {
  propertyId: string;
  name: string;
  /** Remaining usable kWh (capacity * soc% * usable_fraction). */
  remainingKwh: number | null;
  socPct: number;
  status: MapMarkerStatus;
  /** One-line risk message when status is warning or offline. */
  riskMessage: string | null;
  lastPollAt: string | null;
  className?: string;
}

function statusLabel(status: MapMarkerStatus): string {
  switch (status) {
    case 'live':
      return 'Live';
    case 'warning':
      return 'Stale';
    case 'offline':
      return 'Offline';
    default:
      return 'Unknown';
  }
}

function statusBadgeClass(status: MapMarkerStatus): string {
  switch (status) {
    case 'live':
      return 'border-status-online/50 text-status-online';
    case 'warning':
      return 'border-status-stale/50 text-status-stale';
    case 'offline':
      return 'border-status-offline/50 text-status-offline';
    default:
      return 'border-muted-foreground/50 text-muted-foreground';
  }
}

export function PropertyMapHoverCard({
  propertyId,
  name,
  remainingKwh,
  socPct,
  status,
  riskMessage,
  className,
}: PropertyMapHoverCardProps) {
  const navigate = useNavigate();
  const { setPropertyScope } = useApp();

  return (
    <Card
      className={cn(
        'w-64 border-border bg-wt-bg-secondary shadow-lg',
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <CardContent className="p-3">
        <div className="font-medium truncate text-sm">{name}</div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {remainingKwh != null ? (
            <span className="font-mono-numeric text-wt-text-secondary">
              {remainingKwh.toFixed(1)} kWh ({socPct}%)
            </span>
          ) : (
            <span className="font-mono-numeric text-muted-foreground">
              — kWh ({socPct}%)
            </span>
          )}
        </div>
        <div className="mt-1.5">
          <Badge variant="outline" className={cn('text-[10px]', statusBadgeClass(status))}>
            {statusLabel(status)}
          </Badge>
        </div>
        {riskMessage && (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-1">{riskMessage}</p>
        )}
        <button
          type="button"
          onClick={() => {
            setPropertyScope(propertyId, name);
            navigate(`/app/property/${propertyId}`);
          }}
          className="mt-2 flex w-full items-center justify-end gap-0.5 text-xs text-primary hover:underline"
        >
          View property
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </CardContent>
    </Card>
  );
}
